/**
 * Whether an address belongs to a service that only hands out its content to a
 * signed-in user.
 *
 * "Document from URL" fetches the address the way a visitor without an
 * account would (an anonymous HTTP request; the backend adds no credentials).
 * A SharePoint or OneDrive link answers that with a sign-in page or an error,
 * so the attempt used to end in "the document could not be saved" without a
 * reason. For those services SmartY has document sources, which sign in with
 * an application certificate and mirror the permissions; this tells the user
 * so BEFORE the attempt.
 *
 * Only well-known hosts of Microsoft's services are matched. Anything else
 * (an intranet wiki, a company portal) is left to the fetch, which reports a
 * sign-in requirement as such.
 */
const SIGNED_IN_HOST_PATTERNS: readonly RegExp[] = [
  /(^|\.)sharepoint\.com$/i,
  /(^|\.)sharepoint-df\.com$/i,
  /(^|\.)sharepoint\.de$/i,
  /(^|\.)sharepoint\.us$/i,
  /(^|\.)sharepoint\.cn$/i,
  /^onedrive\.live\.com$/i,
  /^1drv\.ms$/i,
  /(^|\.)teams\.microsoft\.com$/i,
];

export type SignedInSource = 'sharepoint' | 'onedrive' | 'teams';

/** The service behind the address, or null when it is not one of the known ones. */
export const signedInSourceOf = (address: string): SignedInSource | null => {
  let host: string;
  try {
    host = new URL(address.trim()).hostname;
  } catch {
    return null;
  }
  if (!SIGNED_IN_HOST_PATTERNS.some(pattern => pattern.test(host))) {
    return null;
  }
  if (/onedrive\.live\.com$|^1drv\.ms$/i.test(host)) {return 'onedrive';}
  if (/teams\.microsoft\.com$/i.test(host)) {return 'teams';}
  return 'sharepoint';
};
